import React from "react";
import { inr } from "@/lib/format";

// Futures spread scenario (backend futures_scenario.py, 27 Oct 2026): what the scanner shows and ranks on.
// Convergence = both legs closed with the spread at zero: entry spread x units, before and after estimated Zerodha
// charges and slippage. A scenario, never a guaranteed or maximum profit. The workbook's Expected P&L is in Info only.
export const SCENARIO_TITLES = {
  "Spread": "Entry spread per unit = SELL-leg price - BUY-leg price, at the executable bid / ask when both quotes are live (else last traded, marked LTP)",
  "Converge P&L": "If the spread closes at ZERO: gross = entry spread x units; net = gross - estimated Zerodha charges (4 orders) - slippage. A scenario, not a guarantee or a maximum",
  "Margin": "Zerodha basket margin for the two-leg position (read-only calculator). Pending until it is fetched - never a workbook estimate",
  "Return on margin": "Net convergence P&L / Zerodha margin, and per day to the near leg's expiry. Blank while the margin is unknown",
};

const sc = (r) => r?.scenario || {};
const ok = (r) => sc(r).status === "OK";

export function BasisTag({ row, testId }) {
  const s = sc(row);
  if (!ok(row)) return null;
  const exec = s.basis === "EXECUTABLE";
  return (
    <span className={`ml-1 text-[9px] font-bold px-1 rounded-sm ${exec ? "bg-[#00C805]/15 text-[#00C805]" : "bg-[#B45309]/15 text-[#B45309]"}`}
      title={exec ? "Priced at the executable bid (SELL) / ask (BUY)" : `Indicative - last traded prices (${(s.basis_reasons || []).join(", ")})`}
      data-testid={testId}>{exec ? "BID/ASK" : "LTP"}</span>
  );
}

export function SpreadEntryCell({ row, testId }) {
  const s = sc(row);
  if (!ok(row)) return <span className="text-slate-600">—</span>;
  return (
    <span data-testid={testId} className={s.entry_spread > 0 ? "" : "text-[#FF6b62]"}
      title={`SELL ${s.sell_price} - BUY ${s.buy_price}${(s.flags || []).length ? ` · ${s.flags.join(", ")}` : ""}`}>
      {Number(s.entry_spread).toFixed(2)}<BasisTag row={row} /></span>
  );
}

export function ConvergeCell({ row, testId }) {
  const s = sc(row);
  if (!ok(row)) return <span className="text-slate-600" title={s.status}>—</span>;
  const c = s.convergence;
  return (
    <span className="flex flex-col items-end leading-tight" data-testid={testId}
      title={`Gross ${inr(c.gross)} - charges ${inr(c.charges)} - slippage ${inr(c.slippage)} (${s.slippage_ticks} tick) = net ${inr(c.net)}. Unchanged spread: ${inr(s.unchanged.net)}.`}>
      <span className={c.net > 0 ? "text-[#00C805]" : "text-[#FF6b62]"}>{inr(c.net)} net</span>
      <span className="text-[10px] text-slate-500">{inr(c.gross)} gross</span>
    </span>
  );
}

export function MarginCell({ row, testId }) {
  const s = sc(row);
  return s.margin != null
    ? <span data-testid={testId} title="Zerodha basket margin, both legs">{inr(s.margin)}</span>
    : <span className="text-slate-600" data-testid={testId} title="Zerodha margin not fetched yet (leading rows are fetched first)">pending</span>;
}

export function ReturnCell({ row, testId }) {
  const s = sc(row);
  if (s.return_on_margin_pct == null) return <span className="text-slate-600" data-testid={testId}>—</span>;
  return (
    <span className="flex flex-col items-end leading-tight" data-testid={testId}>
      <span className={s.return_on_margin_pct > 0 ? "" : "text-[#FF6b62]"}>{s.return_on_margin_pct.toFixed(2)}%</span>
      {s.return_per_day_pct != null && <span className="text-[10px] text-slate-500">{s.return_per_day_pct.toFixed(3)}%/day</span>}
    </span>
  );
}

/** The legacy workbook figures, labelled as a projection (Info view and the Add form). */
export function WorkbookNote({ row }) {
  if (row?.expected_profit == null) return null;
  return (
    <span className="text-[10px] text-slate-500" data-testid="workbook-note"
      title="Excel V2.5 columns J / M / N - in backwardation it assumes the spread moves beyond zero; not used for ranking">
      Workbook projection (legacy): {inr(row.expected_profit)} · est. margin {inr(row.margin)} · {(row.pct_profit * 100).toFixed(2)}%</span>
  );
}
